import { DecimalPipe, UpperCasePipe } from '@angular/common';
import {
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GroupPayConfigStore } from 'app/event/service/grouppay-config.store';
import { OoivRulesStore } from 'app/event/service/ooiv-rules.store';
import { TaxDeterminationStore } from 'app/event/service/tax-determination.store';
import { AuthenticationStore } from '../../../permission/services/authentication.store';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { AdjustmentStore } from '../../service/adjustment.store';
import { PaymentStore } from '../../service/payment.store';
import { catchError, takeUntil } from 'rxjs/operators';
import { AdjustmentSummary } from 'app/event/models/payment-summary/adjustment-summary';
import { PaymentStatus } from 'app/event/models/payment-status.enum';
import { ConfirmationModalComponent } from 'app/shared/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalConfig } from 'app/shared/confirmation-modal/confirmation-modal-config';
import { Party } from 'app/event/models/party';
import { OnyxEvent } from 'app/event/models/event';
import { RoomCommissionType } from 'app/event/models/payment-summary/room-commission-type';
import { PaymentSummary } from 'app/event/models/payment-summary/payment-summary';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { NumberUtils } from 'app/shared/utils/number.util';
import { OoivRule } from 'app/event/models/ooiv-rule';
import { GlobalTaxResponse } from 'app/event/models/TaxResponse';
import { PaymentSummaryStore } from 'app/event/service/payment-summary/payment-summary.store';

@Component({
  selector: 'pioneer-payment-equation',
  templateUrl: './payment-equation.component.html',
  styleUrls: ['./payment-equation.component.css']
})
export class PaymentEquationComponent implements OnInit, OnDestroy {

  @HostBinding('class') defaultClasses = 'col form-container due-amount';
  @HostBinding('class.has-adjust') hasAdjustClass = false;
  @Input()
  @HostBinding('class.status-rejected')
  strikeThroughPaymentLines = false;

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;
  confirmationModalConfig = new ConfirmationModalConfig();

  @Input() strikeThroughFinalPayment = false;

  @Input() vendor: Party;

  @Input() strikeThroughPrepayment = false;

  @Input()
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }
  onyxEvent: OnyxEvent;

  @Input() isEditMode = false;

  @Input() useModifiersMode = false;

  isNotPendingTaxEstimated = true;
  applyOoiv = true;
  ooivRulesValidation = true;

  RoomCommissionType = RoomCommissionType;
  paymentSummary: PaymentSummary;

  private adjustmentToRemove: AdjustmentSummary = null;

  isReducedCommission: boolean;
  showModifiers: boolean;
  showDeleteAdjustmentButton: boolean;
  reducedCommissionTooltip: string;
  showRoomCommissionAmounts: boolean;
  showTaxesSection: boolean;

  private _destroyed$ = new Subject<void>();

  constructor(
    private paymentSummaryStore: PaymentSummaryStore,
    private translateService: TranslateService,
    private errorHandlerService: ErrorHandlerService,
    private taxDeterminationStore: TaxDeterminationStore,
    private groupPayConfigStore: GroupPayConfigStore,
    private paymentStore: PaymentStore,
    private adjustmentStore: AdjustmentStore,
    private authenticationStore: AuthenticationStore,
    private ooivRulesStore: OoivRulesStore,
    private decimalPipe: DecimalPipe,
    private uppercasePipe: UpperCasePipe,
  ) {}

  ngOnInit() {

    if (!this.onyxEvent) {
      const message = this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
        property: 'onyxEvent',
        className: 'PaymentEquationComponent'
      });
      this.errorHandlerService.throw('Input property onyxEvent is null in PaymentEquationComponent', message );
      return;
    }

    if (!this.vendor || !this.vendor.eventPartyId) {
      const message = this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
        property: 'vendor.eventPartyId',
        className: 'PaymentEquationComponent'
      });
      this.errorHandlerService.throw('vendor.eventPartyId is null or undefined', message);
      return;
    }

    this.paymentSummaryStore.getPaymentSummary(this.vendor.eventPartyId)
      .pipe(
        takeUntil(this._destroyed$),
        catchError(err => {
          this.errorHandlerService.throw(err.message, this.translateService.instant('MESSAGES.ERROR.COULD_NOT_GET_PAYMENT_SUMMARY'));
          return Observable.empty();
        })
      )
      .subscribe((paymentSummary: PaymentSummary) => {
        this.paymentSummary = paymentSummary;

        this.showModifiers = this.computeDisplayModifiers(paymentSummary);
        this.hasAdjustClass = this.computeHasAdjustClass(paymentSummary);
        this.isReducedCommission = this.computeReducedCommission(paymentSummary);
        this.showRoomCommissionAmounts = this.computeShowRoomCommissionAmounts(paymentSummary);
        this.showTaxesSection = this.computeShowTaxesSection(paymentSummary);

        if (paymentSummary) {
          this.paymentStore.updateCalculatedPaymentWithTax(this.vendor.eventPartyId, paymentSummary.calculatedPayment);
        }
      });

    this.groupPayConfigStore.isOoivEnabled(this.onyxEvent.eventAgency.code, this.onyxEvent.eventAgency.countryCode, this.vendor.countryCode)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((applyOoiv: boolean) => {
        this.applyOoiv = applyOoiv;
      });

    this.taxDeterminationStore.getGlobalResponseTaxesList()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((taxes: GlobalTaxResponse) => {
        this.isNotPendingTaxEstimated = taxes && taxes.isValidTax;
      });

    if (!this.useModifiersMode) {
      this.ooivRulesStore.getVendorOoivExceptionRules(this.vendor.eventPartyId)
        .pipe(takeUntil(this._destroyed$))
        .subscribe((rule: OoivRule) => {
          this.ooivRulesValidation = rule && rule.ooivExceptionsIsValid;
        });
    }

    this.showDeleteAdjustmentButton = this.computeShowDeleteAdjustmentButton();
    this.reducedCommissionTooltip = this.computeReducedCommissionTooltip();
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private computeHasAdjustClass(paymentSummary: PaymentSummary) {
    if (!paymentSummary) {
      return false;
    }

    if (paymentSummary.adjustmentSummaryList && paymentSummary.adjustmentSummaryList.size > 0) {
      return true;
    }

    if (paymentSummary.prepayment) {
      return true;
    }
    if (this.showModifiers && paymentSummary.modifierSummaryList && paymentSummary.modifierSummaryList.size > 0) {
      return true;
    }

    return false;
  }

  private computeDisplayModifiers(paymentSummary: PaymentSummary) {
    if (!paymentSummary) {
      return false;
    }

    if (!this.useModifiersMode) {
      return false;
    }

    return PaymentStatus.REQUESTED !== paymentSummary.paymentStatus
        && PaymentStatus.RESENT_REQUEST !== paymentSummary.paymentStatus;
  }

  private computeReducedCommission(paymentSummary: PaymentSummary) {
    if (!paymentSummary || !paymentSummary.roomCommissionSummary) {
      return false;
    }
    const roomCommission = paymentSummary.roomCommissionSummary;
    const calculatedCommission = roomCommission.totalSpend * roomCommission.commRate / 100;
    return NumberUtils.round2Fixed(roomCommission.amountDue) < NumberUtils.round2Fixed(calculatedCommission);
  }

  private computeReducedCommissionTooltip() {
    return this.vendor.subscriber ? 'EVENT.COMMISSION.TOOLTIP_REDUCED_COMMISSION_RESERVATIONS'
      : 'EVENT.COMMISSION.TOOLTIP_REDUCED_COMMISSION_RSVPS';
  }

  /**
   *  If it is an agency user and the event property is subscriber,
   *  the addAdjustment button must be disabled
   *  @returns {boolean}
   */
  private computeShowDeleteAdjustmentButton(): boolean {
    if (!this.isEditMode) {
      return false; // hide
    }
    if (this.useModifiersMode) {
      return false; // hide
    }
    if (this.authenticationStore.isAgencyUser() && this.vendor.subscriber) {
      return false; // hide
    }
    return true; // show
  }

  confirmBeforeDeletingAdjustment(adjustment: AdjustmentSummary) {
    this.adjustmentToRemove = adjustment;

    // set up the title of the confirmation modal
    const label = 'EVENT.COMMISSION.DELETE_ADJUSTMENT_CONFIRMATION_TITLE';
    const params = {
      adjustmentAmount: this.decimalPipe.transform(adjustment.amountDue, '1.2-2'),
      currency: this.uppercasePipe.transform(this.onyxEvent.currency)
    };
    this.confirmationModalConfig.title = this.translateService.instant(label, params);

    this.confirmationModal.open();
  }

  deleteAdjustment(adjustmentId: number, eventId: number): void {
    this.adjustmentStore.deleteAdjustment(adjustmentId, eventId, this.vendor.eventPartyId).subscribe();
  }

  confirmBeforeDeletingAdjustmentHandler(value: any) {
    if (value) {
      const adjustmentId = this.adjustmentToRemove.eventAdjustmentId;
      const eventId = this.onyxEvent.eventId;
      this.deleteAdjustment(adjustmentId, eventId);
    }
    this.adjustmentToRemove = null;
  }

  private computeShowRoomCommissionAmounts(paymentSummary: PaymentSummary) {
    if (!this.useModifiersMode) {
      return true; // show
    }

    if (!paymentSummary || !paymentSummary.roomCommissionSummary || !paymentSummary.roomCommissionSummary.amountDue) {
      return false; // hide => show emptyRoomCommissionSummary
    }

    return paymentSummary.roomCommissionSummary.amountDue > 0;
  }

  private computeShowTaxesSection(paymentSummary: PaymentSummary) {
    if (this.useModifiersMode) {
      return true;
    }

    if (!paymentSummary) {
      return false;
    }

    if (paymentSummary.adjustmentSummaryList && paymentSummary.adjustmentSummaryList.size > 0) {
      return true;
    }

    if (paymentSummary.calculatedPayment > 0) {
      return true;
    }

    if (paymentSummary.prepayment) {
      return true;
    }

    return false;
  }

  trackByAdjustmentId(index: number, adjustment: AdjustmentSummary): number {
    return adjustment.eventAdjustmentId;
  }

  getAmount(amount: number, defaultValue: number) {
    return amount ? amount : defaultValue;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/payment/payment-equation/payment-equation.component.ts